import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  AlertCircle,
  CreditCard,
  FileText,
  CheckCircle,
  ArrowLeft,
  Search,
  TrendingDown,
  User,
  XCircle,
  Calendar,
  Users,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  partial: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  unpaid: 'bg-red-100 text-red-800 border-red-300'
};

export default function DuesPage() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const initialSearch = searchParams.get('customer') || '';

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filterStatus, setFilterStatus] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const [payingInvoiceId, setPayingInvoiceId] = useState(null);
  const [quickPayAmount, setQuickPayAmount] = useState('');
  const [quickPayMethod, setQuickPayMethod] = useState('cash');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const [waivingInvoiceId, setWaivingInvoiceId] = useState(null);
  const [waiveLoading, setWaiveLoading] = useState(false);

  useEffect(() => {
    fetchDues();
  }, [periodFilter]);

  const fetchDues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('include_waived', 'true');
      if (periodFilter !== 'all') params.append('period', periodFilter);
      const response = await axios.get(`${API}/invoices/dues/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to load dues' : 'বকেয়া লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const recordQuickPayment = async (invoiceId) => {
    const amount = parseInt(quickPayAmount) || 0;
    if (amount <= 0) {
      toast({ title: language === 'en' ? 'Enter valid amount' : 'সঠিক পরিমাণ লিখুন', variant: 'destructive' });
      return;
    }
    setSubmittingPayment(true);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/payments`, {
        amount,
        payment_method: quickPayMethod,
        notes: null
      });
      toast({ title: language === 'en' ? 'Payment recorded!' : 'পেমেন্ট রেকর্ড হয়েছে!' });
      setPayingInvoiceId(null);
      setQuickPayAmount('');
      fetchDues();
    } catch (error) {
      toast({ title: language === 'en' ? 'Payment failed' : 'পেমেন্ট ব্যর্থ', variant: 'destructive' });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const waiveInvoice = async (invoiceId) => {
    setWaiveLoading(true);
    try {
      await axios.put(`${API}/invoices/${invoiceId}/waive`);
      toast({ title: language === 'en' ? 'Due waived off successfully' : 'বকেয়া মওকুফ হয়েছে' });
      setWaivingInvoiceId(null);
      fetchDues();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to waive off' : 'মওকুফ ব্যর্থ', variant: 'destructive' });
    } finally {
      setWaiveLoading(false);
    }
  };

  const paymentMethods = [
    { value: 'cash', label: language === 'en' ? 'Cash' : 'নগদ' },
    { value: 'bkash', label: 'bKash' },
    { value: 'nagad', label: 'Nagad' },
    { value: 'bank', label: language === 'en' ? 'Bank Transfer' : 'ব্যাংক ট্রান্সফার' },
    { value: 'card', label: language === 'en' ? 'Card' : 'কার্ড' },
  ];

  if (loading && !summary) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  const invoices = summary?.invoices || [];

  const filtered = invoices.filter(inv => {
    const matchesSearch = !searchTerm ||
      inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.job_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.customer_phone?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'amount') return (b.due_amount || 0) - (a.due_amount || 0);
    if (sortBy === 'customer') return (a.customer_name || '').localeCompare(b.customer_name || '');
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const waivedInvoices = (summary?.waived_invoices || []).filter(inv => {
    if (!searchTerm) return true;
    return inv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const topDebtors = summary?.top_debtors || [];

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/invoices`)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
                {language === 'en' ? 'Dues Management' : 'বকেয়া পরিচালনা'}
              </h1>
              <p className="text-slate-500">
                {language === 'en' ? 'Track and settle outstanding payments' : 'বকেয়া পেমেন্ট ট্র্যাক ও মেটান'}
              </p>
            </div>
          </div>
        </div>

        {/* Period Filter Tabs */}
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: language === 'en' ? 'All Time' : 'সর্বকাল' },
            { value: 'today', label: language === 'en' ? 'Today' : 'আজ' },
            { value: 'month', label: language === 'en' ? 'This Month' : 'এই মাস' },
            { value: 'year', label: language === 'en' ? 'This Year' : 'এই বছর' },
          ].map(p => (
            <Button
              key={p.value}
              variant={periodFilter === p.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriodFilter(p.value)}
              className={periodFilter === p.value ? 'bg-blue-800 hover:bg-blue-900' : ''}
            >
              {p.value === 'today' && <Calendar className="w-3.5 h-3.5 mr-1.5" />}
              {p.label}
            </Button>
          ))}
          {loading && <Spinner size="sm" className="ml-2" />}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-xs text-red-600 font-medium">{language === 'en' ? 'Total Outstanding' : 'মোট বকেয়া'}</p>
              </div>
              <p className="text-2xl font-bold text-red-700">{formatCurrency(summary?.total_due || 0, language)}</p>
              <p className="text-xs text-red-500 mt-0.5">{summary?.total_invoices || 0} {language === 'en' ? 'invoices' : 'ইনভয়েস'}</p>
            </CardContent>
          </Card>
          <Card className="border border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-5 h-5 text-yellow-600" />
                <p className="text-xs text-yellow-700 font-medium">{language === 'en' ? 'Partial' : 'আংশিক'}</p>
              </div>
              <p className="text-2xl font-bold text-yellow-800">{invoices.filter(i => i.status === 'partial').length}</p>
              <p className="text-xs text-yellow-600 mt-0.5">
                {formatCurrency(invoices.filter(i => i.status === 'partial').reduce((s, i) => s + (i.due_amount || 0), 0), language)}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-red-500" />
                <p className="text-xs text-red-600 font-medium">{language === 'en' ? 'Fully Unpaid' : 'সম্পূর্ণ অপরিশোধিত'}</p>
              </div>
              <p className="text-2xl font-bold text-red-700">{invoices.filter(i => i.status === 'unpaid').length}</p>
              <p className="text-xs text-red-500 mt-0.5">
                {formatCurrency(invoices.filter(i => i.status === 'unpaid').reduce((s, i) => s + (i.due_amount || 0), 0), language)}
              </p>
            </CardContent>
          </Card>
          <Card className="border border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-5 h-5 text-orange-500" />
                <p className="text-xs text-orange-700 font-medium">{language === 'en' ? 'Waived Off' : 'মওকুফ'}</p>
              </div>
              <p className="text-2xl font-bold text-orange-700">{formatCurrency(summary?.total_waived || 0, language)}</p>
              <p className="text-xs text-orange-500 mt-0.5">{summary?.waived_invoices?.length || 0} {language === 'en' ? 'invoices' : 'ইনভয়েস'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Top Debtors */}
        {!searchTerm && topDebtors.length > 0 && (
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                {language === 'en' ? 'Top Debtors' : 'শীর্ষ ঋণগ্রহীতা'}
              </p>
              <div className="flex flex-wrap gap-2">
                {topDebtors.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => setSearchTerm(d.name)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 hover:bg-red-50 rounded-full border text-sm transition-colors"
                  >
                    <span className="font-medium text-slate-700">{d.name}</span>
                    <span className="text-red-600 font-bold">{formatCurrency(d.total_due, language)}</span>
                    <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5">{d.count}</Badge>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search, Sort & Filter */}
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-10"
              placeholder={language === 'en' ? 'Search customer, invoice, phone or job...' : 'কাস্টমার, ইনভয়েস, ফোন বা জব খুঁজুন...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >✕</button>
            )}
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'en' ? 'All Status' : 'সব স্ট্যাটাস'}</SelectItem>
              <SelectItem value="unpaid">{language === 'en' ? 'Unpaid' : 'অপরিশোধিত'}</SelectItem>
              <SelectItem value="partial">{language === 'en' ? 'Partial' : 'আংশিক'}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">{language === 'en' ? 'Newest First' : 'নতুন আগে'}</SelectItem>
              <SelectItem value="amount">{language === 'en' ? 'Highest Due' : 'সর্বোচ্চ বকেয়া'}</SelectItem>
              <SelectItem value="customer">{language === 'en' ? 'Customer A-Z' : 'কাস্টমার ক-খ'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            {language === 'en'
              ? `Showing ${sorted.length} of ${invoices.length} dues`
              : `${invoices.length} টির মধ্যে ${sorted.length} টি বকেয়া দেখাচ্ছে`}
          </span>
          {searchTerm && (
            <Button variant="ghost" size="sm" onClick={() => setSearchTerm('')} className="text-blue-600 h-6">
              {language === 'en' ? 'Clear search' : 'সার্চ মুছুন'}
            </Button>
          )}
        </div>

        {/* Invoice List */}
        {sorted.length === 0 ? (
          <Card className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-green-700">
              {searchTerm
                ? (language === 'en' ? 'No matching dues found' : 'কোনো মিলে যাওয়া বকেয়া পাওয়া যায়নি')
                : (language === 'en' ? 'No outstanding dues!' : 'কোনো বকেয়া নেই!')}
            </p>
            <p className="text-slate-500">
              {searchTerm
                ? (language === 'en' ? 'Try a different search term' : 'অন্য কিছু দিয়ে খুঁজুন')
                : (language === 'en' ? 'All invoices are fully paid' : 'সব ইনভয়েস পরিশোধিত')}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {sorted.map((inv) => (
              <Card key={inv.id} className="border hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <Link to={`${basePath}/invoices/${inv.id}`} className="font-bold text-blue-700 hover:underline">
                          {inv.invoice_number}
                        </Link>
                        <Badge className={`${statusColors[inv.status]} border text-xs`}>
                          {inv.status === 'partial' ? (language === 'en' ? 'Partial' : 'আংশিক') : (language === 'en' ? 'Unpaid' : 'অপরিশোধিত')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <button
                          onClick={(e) => { e.preventDefault(); setSearchTerm(inv.customer_name); }}
                          className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        >
                          <User className="w-3 h-3" />
                          {inv.customer_name}
                        </button>
                        {inv.job_number && (
                          <Link to={`${basePath}/jobs/${inv.job_id}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                            {inv.job_number}
                          </Link>
                        )}
                        <span className="text-xs text-slate-400">{formatDate(inv.created_at, language)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span>{language === 'en' ? 'Total' : 'মোট'}: <strong>{formatCurrency(inv.total, language)}</strong></span>
                        <span className="text-green-600">{language === 'en' ? 'Paid' : 'পরিশোধিত'}: {formatCurrency(inv.paid_amount, language)}</span>
                        <span className="text-red-600 font-semibold">{language === 'en' ? 'Due' : 'বকেয়া'}: {formatCurrency(inv.due_amount, language)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {payingInvoiceId === inv.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max={inv.due_amount}
                            step="1"
                            className="w-28"
                            placeholder={`${inv.due_amount}`}
                            value={quickPayAmount}
                            onChange={(e) => setQuickPayAmount(e.target.value)}
                            autoFocus
                          />
                          <Select value={quickPayMethod} onValueChange={setQuickPayMethod}>
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {paymentMethods.map((m) => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            onClick={() => recordQuickPayment(inv.id)}
                            disabled={submittingPayment || !quickPayAmount}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {submittingPayment ? <Spinner size="sm" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setPayingInvoiceId(null); setQuickPayAmount(''); }}>
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setPayingInvoiceId(inv.id); setQuickPayAmount(''); setQuickPayMethod('cash'); }}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            {language === 'en' ? 'Pay' : 'পে'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setWaivingInvoiceId(inv.id)}
                            className="text-slate-600 border-slate-300 hover:bg-slate-50"
                            title={language === 'en' ? 'Waive off this due' : 'বকেয়া মওকুফ করুন'}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/invoices/${inv.id}`)}>
                            <FileText className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Waived Invoices Section */}
        {waivedInvoices.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-orange-600" />
                {language === 'en' ? 'Waived Off' : 'মওকুফকৃত'}
                <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-xs">
                  {waivedInvoices.length}
                </Badge>
              </h2>
              {summary?.total_waived > 0 && (
                <span className="text-sm font-medium text-orange-600">
                  {language === 'en' ? 'Total' : 'মোট'}: {formatCurrency(summary.total_waived, language)}
                </span>
              )}
            </div>
            {waivedInvoices.map((inv) => (
              <Card key={inv.id} className="border border-orange-200 bg-orange-50/50 opacity-80 hover:opacity-100 transition-opacity">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <Link to={`${basePath}/invoices/${inv.id}`} className="font-bold text-blue-700 hover:underline">
                          {inv.invoice_number}
                        </Link>
                        <Badge className="bg-orange-100 text-orange-800 border-orange-300 border text-xs">
                          {language === 'en' ? 'Waived' : 'মওকুফ'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {inv.customer_name}
                        </span>
                        {inv.job_number && (
                          <Link to={`${basePath}/jobs/${inv.job_id}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                            {inv.job_number}
                          </Link>
                        )}
                        <span className="text-xs text-slate-400">{formatDate(inv.created_at, language)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span>{language === 'en' ? 'Total' : 'মোট'}: <strong>{formatCurrency(inv.total, language)}</strong></span>
                        <span className="text-orange-600 font-semibold">{language === 'en' ? 'Waived' : 'মওকুফ'}: {formatCurrency(inv.waived_amount, language)}</span>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/invoices/${inv.id}`)}>
                      <FileText className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Waive Confirmation Dialog */}
      <AlertDialog open={!!waivingInvoiceId} onOpenChange={(open) => !open && setWaivingInvoiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'en' ? 'Waive Off This Due?' : 'বকেয়া মওকুফ করবেন?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en'
                ? 'This will forgive the remaining due amount. The invoice will be marked as paid with a "Waived" badge. The waived amount will be recorded for your records.'
                : 'এটি অবশিষ্ট বকেয়া পরিমাণ মওকুফ করবে। ইনভয়েসটি "মওকুফ" ব্যাজসহ পরিশোধিত হিসেবে চিহ্নিত হবে।'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={waiveLoading}>
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => waiveInvoice(waivingInvoiceId)}
              disabled={waiveLoading}
              className="bg-slate-700 hover:bg-slate-800"
            >
              {waiveLoading ? <Spinner size="sm" /> : (language === 'en' ? 'Waive Off' : 'মওকুফ')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
